import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertOctagon, RotateCcw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an exception:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-brand-charcoal flex items-center justify-center p-6 font-mono text-brand-sand">
          <div className="max-w-md w-full border border-brand-burgundy bg-brand-espresso/20 p-8 shadow-2xl backdrop-blur-sm rounded-sm text-center">
            <div className="flex justify-center mb-4">
              <AlertOctagon className="w-12 h-12 text-brand-orange animate-pulse" />
            </div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-white mb-2">
              System Boundary Intercept
            </h2>
            <p className="text-xs text-brand-sand/60 mb-6 leading-relaxed">
              An unhandled rendering exception occurred inside the Admin Hub. The runtime state was automatically frozen to prevent data corruption.
            </p>
            {this.state.error && (
              <div className="bg-brand-espresso/40 border border-brand-espresso p-3 rounded-sm text-[10px] text-left text-brand-orange mb-6 overflow-x-auto subtle-scrollbar font-bold">
                {this.state.error.toString()}
              </div>
            )}
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-brand-orange bg-brand-orange/10 hover:bg-brand-orange/20 text-white text-xs font-bold transition-all rounded-sm cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Force Reset State
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
export default ErrorBoundary;
